import { ArrowRight } from 'lucide-react';

import { FULL_DATETIME_FORMAT } from '@/lib/constants';
import { ShipmentChangeModel, ShipmentModel } from '@/lib/models/shipment/types';
import { formatDateForUser, formatDuration } from '@/utils/format';
import { tv } from '@/utils/styles';

export interface ShipmentHistoryItemProps {
  original: ShipmentChangeModel['original'];
  updated: ShipmentChangeModel['updated'];
  itemKey: keyof ShipmentModel;
}

const formatDefaultValue = (value?: string) => (value ? String(value) : 'None');
const formatDateValue = (value?: string) => (value ? formatDateForUser(value, FULL_DATETIME_FORMAT) : 'None');
const formatDelayValue = (value?: string) => {
  if (!value) return 'None';
  const valueInt = parseInt(value, 10);

  return `${formatDuration(Math.abs(valueInt) * 60)}${valueInt < 0 ? ' ahead of schedule' : ''}`;
};

const fieldFormatter: {
  [K in keyof ShipmentModel]?: { label: string; formatValue?: (value: string) => string };
} = {
  ata: { label: 'ATA', formatValue: formatDateValue },
  atd: { label: 'ATD', formatValue: formatDateValue },
  eta: { label: 'ETA', formatValue: formatDateValue },
  etd: { label: 'ETD', formatValue: formatDateValue },
  status: { label: 'Status' },
  carrier: { label: 'Carrier' },
  subStatus: { label: 'Sub status' },
  originPortCode: { label: 'Origin port code' },
  originPortName: { label: 'Origin port name' },
  destinationPortCode: { label: 'Destination port code' },
  destinationPortName: { label: 'Destination port name' },
  delay: { label: 'Delay', formatValue: formatDelayValue },
};

const ShipmentHistoryItem = ({ original, updated, itemKey }: ShipmentHistoryItemProps) => {
  const { label = itemKey, formatValue = formatDefaultValue } = fieldFormatter[itemKey] || {};
  const originalValue = original[itemKey] as string;
  const updatedValue = updated[itemKey] as string;

  const { item } = styles();

  return (
    <li key={itemKey} className={item()}>
      <span className="font-bold">{label}</span> changed from {formatValue(originalValue)}{' '}
      <ArrowRight size={14} className="mb-0.5 align-middle" />{' '}
      <span className="font-bold">{formatValue(updatedValue)}</span>
    </li>
  );
};

const styles = tv({
  slots: {
    heading: 'mb-1',
    item: 'text-sm',
    list: 'list-inside list-disc',
  },
});

export { ShipmentHistoryItem };
